<template>
  <div class="v3-form polarity-message-form">
    <div class="row">
      <div>
        <Label required html-for="contact-control-student-a-select"
          >Student A</Label
        >
        <LazyLoadSelect
          input-id="contact-control-student-a-select"
          accessible-label="Student A"
          hide-floating-label
          append-dropdown-to-body
          :required="true"
          :classes="'mb-3'"
          :selected="state.form.studentA"
          type="student"
          placeholder="Find Student"
          :invalid-feedback="
            state.serverErrors.studentA || clientFieldErrors.studentA
          "
          @changed="onStudentAChange"
        ></LazyLoadSelect>
      </div>
      <div>
        <Label required html-for="contact-control-student-b-select"
          >Student B</Label
        >
        <LazyLoadSelect
          input-id="contact-control-student-b-select"
          accessible-label="Student B"
          hide-floating-label
          append-dropdown-to-body
          :required="true"
          :classes="'mb-3'"
          :selected="state.form.studentB"
          type="student"
          placeholder="Find Student"
          :invalid-feedback="
            state.serverErrors.studentB || clientFieldErrors.studentB
          "
          @changed="onStudentBChange"
        ></LazyLoadSelect>
      </div>
      <div>
        <Label label-id="cc-admin-note-lbl" html-for="cc-admin-note"
          >Admin note</Label
        >
        <InputField
          id="cc-admin-note"
          label-id="cc-admin-note-lbl"
          type="textarea"
          v-model="state.form.note"
          class="d-block"
          :rows="5"
          :invalid-feedback="clientFieldErrors.note"
        ></InputField>
      </div>
      <div
        v-if="state.serverRes"
        ref="successMessageRegionRef"
        class="mt-4"
        tabindex="-1"
        role="status"
        aria-live="assertive"
        aria-atomic="true"
      >
        <InfoBox
          ref="responseInfoBoxRef"
          class="mt-0"
          :class="{ danger: Object.keys(state.serverErrors).length }"
          :title="state.serverRes.title"
          >{{ state.serverRes.message }}</InfoBox
        >
      </div>
      <div
        class="d-flex align-items-center justify-content-center mt-4 text-center"
      >
        <LoadingButton
          v-if="!isHeaderCreateButton"
          class="me-2 px-4"
          @click="onCancel"
          rounded
          >Cancel</LoadingButton
        >
        <LoadingButton
          :is-loading="state.isProcessing"
          @click="submit()"
          class="px-4 me-3"
          solid
          rounded
          >Create</LoadingButton
        >
        <router-link
          v-if="isHeaderCreateButton"
          class="text-graident-blue d-flex flex-row-reverse"
          to="/limits/ab-polarity"
        >
          <div class="d-flex align-items-center" @click="onCancel()">
            <img
              width="18"
              class="m-0 me-2"
              src="@/assets/images/icons/gradient-link.png"
            />
            <span class="text-graident-blue"
              ><span class="text-graident-blue">Go to full menu</span></span
            >
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, inject, nextTick, onUnmounted, ref } from "vue"
import { useStore } from "vuex"
import Label from "@/v3components/shared/Form/Label.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"

const SUCCESS_MODAL_CLOSE_DELAY_MS = 2400

export default {
  name: "CreateContactControlForm",
  components: {
    Label,
    LazyLoadSelect,
    InputField,
    InfoBox,
    LoadingButton
  },
  props: {
    isHeaderCreateButton: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel", "closeModal"],
  setup(props, { emit }) {
    const store = useStore()
    const modal = inject("modal")
    const responseInfoBoxRef = ref(null)
    const successMessageRegionRef = ref(null)
    let successCloseTimerId = null

    const clearSuccessCloseTimer = () => {
      if (successCloseTimerId != null) {
        clearTimeout(successCloseTimerId)
        successCloseTimerId = null
      }
    }

    const focusSuccessMessageRegion = async () => {
      await nextTick()
      requestAnimationFrame(() => {
        const el = successMessageRegionRef.value
        if (!el || typeof el.focus !== "function") return
        try {
          el.focus({ preventScroll: true })
        } catch {
          el.focus()
        }
      })
    }

    const scheduleModalCloseAfterSuccess = () => {
      clearSuccessCloseTimer()
      successCloseTimerId = window.setTimeout(() => {
        successCloseTimerId = null
        emit("closeModal")
        emit("cancel")
      }, SUCCESS_MODAL_CLOSE_DELAY_MS)
    }

    const state = reactive({
      isProcessing: false,
      form: {
        studentA: null,
        studentB: null,
        note: ""
      },
      serverErrors: {},
      serverRes: null
    })

    const validationMessages = {
      required: "This field is required"
    }

    const clientFieldErrors = reactive({
      studentA: "",
      studentB: "",
      note: ""
    })

    const clearClientFieldErrors = () => {
      clientFieldErrors.studentA = ""
      clientFieldErrors.studentB = ""
      clientFieldErrors.note = ""
    }

    const onStudentAChange = (value) => {
      state.form.studentA = value
      clientFieldErrors.studentA = ""
      if (state.serverErrors.studentA) {
        const next = { ...state.serverErrors }
        delete next.studentA
        state.serverErrors = next
      }
    }

    const onStudentBChange = (value) => {
      state.form.studentB = value
      clientFieldErrors.studentB = ""
      if (state.serverErrors.studentB) {
        const next = { ...state.serverErrors }
        delete next.studentB
        state.serverErrors = next
      }
    }

    const submit = async () => {
      state.serverRes = null
      state.serverErrors = {}
      clearClientFieldErrors()

      let valid = true
      if (!state.form.studentA) {
        clientFieldErrors.studentA = validationMessages.required
        valid = false
      }
      if (!state.form.studentB) {
        clientFieldErrors.studentB = validationMessages.required
        valid = false
      }
      if (!valid) {
        return
      }
      const data = {
        first_user_id: state.form.studentA
          ? state.form.studentA.value.id
          : state.form.studentA,
        second_user_id: state.form.studentB
          ? state.form.studentB.value.id
          : state.form.studentB,
        status: false,
        message: state.form.note
      }
      createPolarity(data)
    }

    const refreshPolarities = async () => {
      const response = await store.dispatch("polarities/getPolarities")
      const data = response?.data
      if (data?.data) {
        store.commit("polarities/SET_POLARITIES", data.data)
      }
    }

    const createPolarity = async (data) => {
      try {
        state.isProcessing = true
        await store.dispatch("polarities/createPolarity", data)
        state.serverErrors = {}
        modal?.setAction?.("createContactControl")
        await refreshPolarities()
        resetFormData()
        setResponseInfoBox("Success", "Contact control created.")
        await focusSuccessMessageRegion()
        await nextTick()
        scheduleModalCloseAfterSuccess()
      } catch (err) {
        clearSuccessCloseTimer()
        const res = err?.response?.data || {}
        const rawErrors =
          res?.errors &&
          typeof res.errors === "object" &&
          !Array.isArray(res.errors)
            ? res.errors
            : {}
        const mapped = {}
        if (rawErrors.first_user_id) {
          const v = rawErrors.first_user_id
          mapped.studentA = Array.isArray(v) ? v[0] : String(v)
        }
        if (rawErrors.second_user_id) {
          const v = rawErrors.second_user_id
          mapped.studentB = Array.isArray(v) ? v[0] : String(v)
        }
        state.serverErrors = mapped
        const msg =
          typeof res?.message === "string"
            ? res.message
            : Array.isArray(res?.message)
              ? res.message.filter(Boolean).join(" ")
              : res?.message
                ? String(res.message)
                : "Request failed"
        setResponseInfoBox("Error", msg)
        await focusSuccessMessageRegion()
      } finally {
        state.isProcessing = false
      }
    }

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }

    const onCancel = () => {
      clearSuccessCloseTimer()
      resetFormData()
      emit("cancel")
      emit("closeModal")
    }

    onUnmounted(() => {
      clearSuccessCloseTimer()
    })

    const resetFormData = () => {
      state.form = {
        studentA: null,
        studentB: null,
        note: ""
      }
      clearClientFieldErrors()
    }

    return {
      state,
      clientFieldErrors,
      responseInfoBoxRef,
      successMessageRegionRef,
      submit,
      setResponseInfoBox,
      onCancel,
      resetFormData,
      onStudentAChange,
      onStudentBChange
    }
  }
}
</script>
